/**
 * Wizard state for the letter generator, and the one place it becomes a
 * `LetterRequestBody`.
 *
 * Deliberately dumb: this module trims strings and splits lines. It does not
 * decide whether a statement is permitted, whether evidence is sufficient, or
 * whether the firm may issue — those are the server's calls, and duplicating
 * them here would produce a UI that refuses for reasons the engine doesn't hold
 * (or worse, passes something the engine would refuse and calls it valid).
 */
import type { LetterRequestBody, ParsedFigure, StatementInput } from '@/components/attest/api'

export interface EvidenceDraft {
  source_type: string
  reference: string
  location: string
  obtained_date: string
  /**
   * Provenance of an uploaded document (ATTEST-EVIDENCE-INTEGRITY-1). Local to
   * the wizard and never sent: `location` already carries the blob id, which is
   * the part the letter cites. These exist so the CPA can see *what the parser
   * actually read* while they write the sentence that cites it — and so the
   * figure menu survives a re-render.
   */
  filename?: string
  documentKind?: string
  parsed?: boolean
  figures?: ParsedFigure[]
  readNote?: string
  /**
   * ATTEST-EVIDENCE-REQUIRED-1 item 3b — the figure the CPA clicked out of this
   * document, and what it read at that moment. The exception to the rule above:
   * these two *are* sent, because they are the only record of which document a
   * number was attributed to. The sentence carries "$1,284,300" and no trace of
   * where it came from; the server re-reads this document at build and confirms
   * the key still resolves to this value.
   *
   * Absent when the number was typed by hand. That is not a gap — the server
   * still checks typed figures against the documents as a whole. This pair only
   * buys the narrower check: the file changing under a drafted letter.
   */
  figure_key?: string
  figure_value?: number | null
}

export interface StatementDraft {
  assertion_id: string
  text: string
  evidence: EvidenceDraft[]
  /**
   * ATTEST-EVIDENCE-REQUIRED-1 — the flag-and-attest tick. Unlike the UI-local
   * fields on `EvidenceDraft`, this one *is* sent: it is the CPA's declaration
   * that the statement rests on records in their own file, and the whole point
   * of asking is that the answer gets recorded against their name.
   *
   * Only ever offered for an assertion the policy marks
   * `requires_attached_source: false`. The checklist decides that per assertion;
   * this field just carries whatever was ticked.
   */
  supportedByRecordsOnFile: boolean
}

export interface LetterFormState {
  subjectEntity: string
  addressee: string
  intendedUsersText: string
  purpose: string
  inputs: Record<string, string>
  statements: StatementDraft[]
  letterDate: string
  signerTitle: string
  issuerAddressText: string
  engagementState: string
  clientConsentOnFile: boolean
}

/**
 * A patch to `LetterFormState` — either the fields themselves, or a function
 * that derives them from the *current* state.
 *
 * The function form exists because several fields on this form are nested
 * (`inputs` is a record, `statements` is a list), so writing one of them means
 * re-merging its container. Computing that container from the `form` prop reads
 * a render-time snapshot, and any two patches that share a snapshot lose the
 * earlier one — the later merge overwrites the whole container.
 *
 * Today's UI does not hit that: every write comes from a discrete DOM event,
 * which React flushes synchronously, so the next handler already sees fresh
 * props. The hazard is one call from an async or batched context away — a
 * debounced autosave, a paste handler, an "apply suggested values" button — and
 * the failure is silent data loss on a compliance form. Deriving from `prev`
 * costs nothing and removes the class.
 */
export type LetterFormPatch =
  | Partial<LetterFormState>
  | ((prev: LetterFormState) => Partial<LetterFormState>)

/**
 * What the *caller* declares about this engagement.
 *
 * Deliberately no firm name, signer name or licence state: signing identity is
 * read from the active firm profile on the server
 * (ATTEST-EVIDENCE-INTEGRITY-1a). A form field for it would be a field that
 * changes whose name and licence number appear over the signature, which is
 * exactly the thing a letter's reader is trusting. What remains here is what
 * only the engagement's own CPA can answer — independence, peer review — and
 * the server keeps every one of those.
 */
export interface IssuerFormState {
  attestedNoImpairingInterest: boolean
  licensedForAttest: boolean
  peerReviewCurrent: boolean
  peerReviewExpiry: string
  isPlatformBackOffice: boolean
  declaredInterests: Array<{
    subject_entity: string
    interest_type: string
    description: string
  }>
}

export const EMPTY_EVIDENCE: EvidenceDraft = {
  source_type: 'document',
  reference: '',
  location: '',
  obtained_date: '',
}

export function emptyStatement(assertionId = ''): StatementDraft {
  return {
    assertion_id: assertionId,
    text: '',
    evidence: [{ ...EMPTY_EVIDENCE }],
    // Silence, not consent. A new row has attested to nothing.
    supportedByRecordsOnFile: false,
  }
}

export const EMPTY_LETTER_FORM: LetterFormState = {
  subjectEntity: '',
  addressee: '',
  intendedUsersText: '',
  purpose: '',
  inputs: {},
  statements: [emptyStatement()],
  letterDate: '',
  signerTitle: 'Certified Public Accountant',
  issuerAddressText: '',
  engagementState: '',
  clientConsentOnFile: false,
}

export const EMPTY_ISSUER_FORM: IssuerFormState = {
  attestedNoImpairingInterest: false,
  licensedForAttest: true,
  peerReviewCurrent: true,
  peerReviewExpiry: '',
  isPlatformBackOffice: false,
  declaredInterests: [],
}

function lines(text: string): string[] {
  return text.split('\n').map(l => l.trim()).filter(Boolean)
}

/** Commas or newlines — people paste intended-user lists both ways. */
function listField(text: string): string[] {
  return text.split(/[\n,]/).map(l => l.trim()).filter(Boolean)
}

function toStatement(draft: StatementDraft): StatementInput {
  return {
    assertion_id: draft.assertion_id.trim(),
    text: draft.text.trim(),
    evidence: draft.evidence
      .filter(e => e.reference.trim())
      .map(e => ({
        source_type: e.source_type.trim(),
        reference: e.reference.trim(),
        location: e.location.trim(),
        obtained_date: e.obtained_date.trim() || null,
        // Only when there was a click. Sending `''` would be indistinguishable
        // from one, and the server treats a key it cannot find in the document
        // as a refusal — so an invented empty key would refuse every letter
        // whose figures were typed rather than clicked.
        ...(e.figure_key
          ? { figure_key: e.figure_key, figure_value: e.figure_value ?? null }
          : {}),
      })),
    // Sent on every statement, not only the ones that ticked it: `false` is a
    // recorded "no", and a field that appears only when true would leave the
    // artifact unable to tell a declined attestation from an old client.
    supported_by_records_on_file: draft.supportedByRecordsOnFile,
  }
}

/**
 * `engagementBasis` is passed through untouched (ATTEST-ENGAGEMENT-BASIS-2).
 * An empty string means "the type's declared default" and is sent as null —
 * the same thing a pre-basis caller sends, which is how the AUP basis stays
 * the behaviour of every existing request.
 */
export function buildLetterRequest(
  letterTypeId: string,
  form: LetterFormState,
  issuer: IssuerFormState,
  engagementBasis = '',
): LetterRequestBody {
  return {
    letter_type: letterTypeId,
    engagement_basis: engagementBasis.trim() || null,
    subject_entity: form.subjectEntity.trim(),
    addressee: form.addressee.trim(),
    intended_users: listField(form.intendedUsersText),
    purpose: form.purpose.trim(),
    statements: form.statements
      .filter(s => s.assertion_id.trim() || s.text.trim())
      .map(toStatement),
    issuer: {
      // Identity is intentionally absent — the server overwrites it from the
      // active firm profile and refuses outright when there isn't one. Sending
      // a name here would be sending a name the letter will not use.
      attested_no_impairing_interest: issuer.attestedNoImpairingInterest,
      declared_interests: issuer.declaredInterests
        .filter(i => i.subject_entity.trim() && i.interest_type.trim())
        .map(i => ({
          subject_entity: i.subject_entity.trim(),
          interest_type: i.interest_type.trim(),
          description: i.description.trim(),
        })),
      licensed_for_attest: issuer.licensedForAttest,
      // The licence *state* comes from the profile with the licence number, so
      // the two can never disagree. An empty list is what the server resolves.
      license_states: [],
      peer_review_current: issuer.peerReviewCurrent,
      peer_review_expiry: issuer.peerReviewExpiry.trim() || null,
      is_platform_back_office: issuer.isPlatformBackOffice,
    },
    inputs: Object.fromEntries(
      Object.entries(form.inputs).map(([k, v]) => [k, v.trim()]),
    ),
    letter_date: form.letterDate.trim() || null,
    signer_title: form.signerTitle.trim() || 'Certified Public Accountant',
    issuer_address: lines(form.issuerAddressText),
    engagement_state: form.engagementState.trim() || null,
    client_consent_on_file: form.clientConsentOnFile,
  }
}
